/* eslint-disable react-hooks/rules-of-hooks */
import { formatTimeToISO } from 'formatters'
import { ServerSidePropsContext } from 'providers/ServerSideProps'
import { SeoProviderProps } from 'providers/Seo/types'
import { useContext } from 'react'
import { useImageProxy } from 'hooks/useImageProxy'
import { getPageTitleFromPath } from '../../utils/getDepFromNav'
import { getThumbUrl } from "../../utils/getThumbUrl";

export const useSeoStrategy = (): SeoProviderProps['data'] => {

  const serverSideContext = useContext(ServerSidePropsContext)
  const strategy_type = serverSideContext?.pageType?.type || 'home'
  const domain = serverSideContext.domain
  const site = serverSideContext.site
  const page = serverSideContext.page
  const article = serverSideContext?.article?.[0] || {}
  const thumbData = getThumbUrl(site?.pathSegment)

  const nav = serverSideContext.navigation || {}
  const fullNav: any = []

  const menuActions = nav?.menuActions || []
  const menuSide = nav?.menuSide || []
  const menuTop = nav?.menuTop || []
  const prevNav = [...menuActions, ...menuSide, ...menuTop]

  for (let i = 0; i < prevNav.length; i++) {
    const it = prevNav[i]
    fullNav.push({
      name: it?.name,
      path: it?.path
    })
    const subItems: any = it?.subItems || []
    for (let j = 0; j < subItems.length; j++) {
      const sit = subItems[j]
      fullNav.push({
        name: sit?.name,
        path: sit?.path
      })
    }
  }

  function parseMainSection(path) {
    const navItems = path ? getPageTitleFromPath(fullNav, path) : []
    return navItems[0] ? navItems[0] : ''
  }

  function parseArticleKeywords(tags) {
    if (!tags) {
      return ''
    }
    return tags.join(', ')
  }

  function parseComposedTitle(title, path, siteName) {
    const navItems = path ? getPageTitleFromPath(fullNav, path) : []
    const parsed = navItems.reverse()
    const composedTitle = parsed.join(' | ')
    return `${title ? title : composedTitle} | ${siteName}`
  }

  function checkMediaVideoType(data: Array<any>) {
    const regexToMatch = new RegExp('class="youtube-video"')
    const regexToMatchUrl = new RegExp(/src="([^"]*)"/)
    const embeds = data?.filter((i) => i?.inputTemplate === 'br.com.atex.templates.articleEmbed.it')
    const videoComponents = data?.filter((i) => i?.inputTemplate === 'br.com.atex.templates.articleVideo.it')
    const video = embeds?.find((i) => i?.slotTemplateCode.match(regexToMatch))
    const videoComponent = videoComponents && videoComponents[0] || null
    const url = video?.slotTemplateCode?.match(regexToMatchUrl) || []

    if (!video?.inputTemplate && !videoComponent?.inputTemplate) {
      return {
        enabled: false,
        name: '',
        publishedDate: null,
        url: ''
      }
    } else {
      if (video?.inputTemplate) {
        return {
          enabled: true,
          name: video.name,
          publishedDate: new Date(video?.publishingTime).toISOString(),
          url: url.length > 1 ? url[1] : ''
        }
      }
      if (videoComponent?.inputTemplate) {
        return {
          enabled: true,
          name: videoComponent?.name || '',
          publishedDate: new Date(videoComponent?.publishingTime).toISOString(),
          url: `https://cdn.ojc.com.br/videos/mp4/${videoComponent?.link}` || ''
        }
      }
      return {
        enabled: false,
        name: '',
        publishedDate: null,
        url: ''
      }
    }
  }

  if (strategy_type === 'article') {
    const hasMediaVideoType = checkMediaVideoType(article.bodyItems)
    const images: Array<any> = []
    const sImages: Array<string> = []
    if (article?.images && article?.images?.length > 0) {
      article.images.forEach((i) => {
        const baseUrl = `${domain}${useImageProxy(`${i.url}f=3x2`, true)}`
        images.push({
          url: baseUrl,
          secureUrl: baseUrl,
          type: 'image/jpg',
          alt: i.title
        })
        sImages.push(baseUrl)
      })
    }
    return {
      viewport: {
        enabled: true,
        initialScale: '1',
        minimumScale: '1',
        shrinkToFit: true,
        viewportFit: 'cover',
        width: 'device-width'
      },
      title: {
        enabled: true,
        title: article?.metadataTitle !== '' ? article.metadataTitle : `${article.name} | ${site.name}`
      },
      description: {
        enabled: true,
        description: article?.metadataDescription !== '' ? article.metadataDescription : article?.lead ? article.lead : ''
      },
      canonical: {
        enabled: true,
        url: `${domain}${article?.path}`
      },
      keywords: {
        enabled: true,
        keywords: parseArticleKeywords(article?.tags?.tags || null)
      },
      openGraphArticle: {
        enabled: true,
        pageModifiedDate: formatTimeToISO(article?.modificationTime),
        pagePublishedDate: formatTimeToISO(article?.publishingTime),
        pageKeywords: article?.metadataKeywords || article?.tags || [],
        section: parseMainSection(page.path),
      },
      openGraph: {
        enabled: true,
        pageDescription: article?.metadataDescription || article.lead,
        pageLocale: 'pt_BR',
        pageTitle: article?.metadataTitle !== '' ? article?.metadataTitle : article?.name,
        pageType: 'article',
        pageUrl: `${domain}${article.path}`,
        siteName: site?.name
      },
      openGraphImages: {
        enabled: true,
        pageImages: images?.length > 0 ? images : [
          {
            secureUrl: `${domain}${thumbData?.url}`,
            url: `${domain}${thumbData?.url}`,
            type: thumbData?.type,
            height: thumbData?.height,
            width: thumbData?.width
          }
        ]
      },
      facebook: {
        enabled: true,
        appId: site?.pageSocialData?.facebookAppId,
        pageId: site?.pageSocialData?.facebookPageId
      },
      twitter: {
        enabled: true,
        cardFormat: 'summary_large_image',
        creatorUsername: site?.pageSocialData?.twitterContentCreator,
        description: article?.metadataDescription !== '' ? article?.metadataDescription : article?.lead ? article.lead : '',
        imageUrl: images?.length > 0 ? images[0].url : `${domain}${thumbData?.url}`,
        siteUsername: site?.pageSocialData?.twitterSiteCreator,
        title: article?.metadataTitle !== '' ? article?.metadataTitle : `${article?.name} | ${site?.name}`,
        url: site?.siteDomain
      },
      gjcAnalytics: {
        enabled: true,
        responsavel: article?.author?.name || '',
        authorOrigin: 'redacao',
        articleEditoria: page?.name,
        imageUrl: images?.length > 0 ? images[0].url : `${domain}${thumbData?.url}`,
        sectionUrl: `${site?.siteDomain}${page.path}`,
      },
      author: {
        enabled: true,
        author:  article?.author?.name || '',
      },
      schemas: {
        enabled: true,
        article: {
          enabled: true,
          author: { '@type': 'Person', 'name': article?.author?.name || '' },
          articleBody: article?.bodyParsed,
          dateModified: formatTimeToISO(article?.modificationTime),
          datePublished: formatTimeToISO(article?.publishingTime),
          description: article?.lead,
          headline: article?.metadataTitle !== '' ? article?.metadataTitle : article?.name,
          image: sImages,
          mainEntityOfPage: `${site?.siteDomain}${article?.path}`,
          publisher: { '@type': 'Organization', 'name': site?.pageTitle, logo: { '@type': 'ImageObject', 'url': `${site?.siteDomain}/assets/opopular/favicons/96x96.png`, 'height': '96', 'width': '96' } },
          video: hasMediaVideoType?.enabled ? { '@type': 'VideoObject', 'url': hasMediaVideoType.url, 'name': hasMediaVideoType?.name, datePublished: hasMediaVideoType?.publishedDate } : undefined
        },
        organization: {
          enabled: true,
          name: site?.pageTitle,
          url: site?.siteDomain,
          logo: { '@type': 'ImageObject', 'url': `${site?.siteDomain}/assets/opopular/favicons/96x96.png`, 'height': '96', 'width': '96' },
          parentOrganization: { '@type': 'Organization', '@id': 'https://www.gjccorp.com.br/', 'name': 'Grupo Jaime Câmara' }
        },
        webpage: {
          enabled: false,
        },
        website: {
          enabled: true,
          name: site?.pageTitle,
          description: site?.pageMetadata,
          url: site?.siteDomain,
        }
      }
    }
  }
  else if (strategy_type === 'department') {
    return {
      viewport: {
        enabled: true,
        initialScale: '1',
        minimumScale: '1',
        shrinkToFit: true,
        viewportFit: 'cover',
        width: 'device-width'
      },
      title: {
        enabled: true,
        title: parseComposedTitle(page?.pageTitle !== '' ? page.pageTitle : '', page?.pageTitle !== '' ? '' : page.path, site?.name)
      },
      description: {
        enabled: true,
        description: page?.pageMetadata !== '' ? page.pageMetadata : article?.lead 
      },
      canonical: {
        enabled: true,
        url: `${domain}${page?.path}`
      },
      keywords: {
        enabled: true,
        keywords: page?.pageKeywords
      },
      openGraph: {
        enabled: true,
        pageDescription: page?.pageMetadata,
        pageLocale: 'pt_BR',
        pageTitle: parseComposedTitle(page?.pageTitle !== '' ? page.pageTitle : '', page?.pageTitle !== '' ? '' : page.path, site.name),
        pageType: 'website',
        pageUrl: `${domain}${serverSideContext.page.path}`,
        siteName: site.name
      },
      openGraphImages: {
        enabled: true,
        pageImages: [
          {
            secureUrl: `${domain}${thumbData.url}`,
            url: `${domain}${thumbData.url}`,
            type: thumbData.type,
            height: thumbData.height,
            width: thumbData.width
          }
        ]
      },
      facebook: {
        enabled: true,
        appId: site.pageSocialData.facebookAppId,
        pageId: site.pageSocialData.facebookPageId
      },
      twitter: {
        enabled: true,
        cardFormat: 'summary_large_image',
        description: page.pageMetadata !== '' ? page.pageMetadata : article.lead,
        imageUrl: `${domain}${thumbData.url}`,
        siteUsername: site.pageSocialData.twitterSiteCreator,
        title: parseComposedTitle(page.pageTitle !== '' ? page.pageTitle : '', page.pageTitle !== '' ? '' : page.path, site.name),
        url: site.siteDomain
      },
      schemas: {
        enabled: true,
        article: {
          enabled: false
        },
        organization: {
          enabled: true,
          name: site.pageTitle,
          url: site.siteDomain,
          logo: { '@type': 'ImageObject', 'url': `${site.siteDomain}/assets/opopular/favicons/96x96.png`, 'height': '96', 'width': '96' },
          parentOrganization: { '@type': 'Organization', '@id': 'https://www.gjccorp.com.br/', 'name': 'Grupo Jaime Câmara' }
        },
        webpage: {
          enabled: true,
          name: page.pageTitle !== '' ? page.pageTitle : page.name,
          description: page.pageMetadata,
          url: `${site.siteDomain}${page.path}`,
          image: `${site.siteDomain}/assets/opopular/banners/BannerSocialShare.png`
        },
        website: {
          enabled: true,
          name: site.pageTitle,
          description: site.pageMetadata,
          url: site.siteDomain,
        }
      }
    }
  }
  else if (strategy_type === 'home') {
    return {
      viewport: {
        enabled: true,
        initialScale: '1',
        minimumScale: '1',
        shrinkToFit: true,
        viewportFit: 'cover',
        width: 'device-width'
      },
      title: {
        enabled: true,
        title: site?.pageTitle !== '' ? site.pageTitle : site.name
      },
      description: {
        enabled: true,
        description: site.pageMetadata
      },
      canonical: {
        enabled: true,
        url: `${domain}${site.path}`
      },
      keywords: {
        enabled: true,
        keywords: site.pageKeywords
      },
      openGraph: {
        enabled: true,
        pageDescription: site.pageMetadata,
        pageLocale: 'pt_BR',
        pageTitle: site?.pageTitle !== '' ? site.pageTitle : site.name,
        pageType: 'website',
        pageUrl: `${domain}${serverSideContext.site.path}`,
        siteName: site.name
      },
      openGraphImages: {
        enabled: true,
        pageImages: [
          {
            secureUrl: `${domain}${thumbData.url}`,
            url: `${domain}${thumbData.url}`,
            type: thumbData.type,
            height: thumbData.height,
            width: thumbData.width
          }
        ]
      },
      facebook: {
        enabled: true,
        appId: site.pageSocialData.facebookAppId,
        pageId: site.pageSocialData.facebookPageId
      },
      twitter: {
        enabled: true,
        cardFormat: 'summary_large_image',
        description: site.pageMetadata,
        imageUrl: `${domain}${thumbData.url}`,
        siteUsername: site.pageSocialData.twitterSiteCreator,
        title: site?.pageTitle !== '' ? site.pageTitle : site.name,
        url: site.siteDomain
      },
      schemas: {
        enabled: true,
        article: {
          enabled: false
        },
        organization: {
          enabled: true,
          name: site?.pageTitle,
          url: site.siteDomain,
          logo: { '@type': 'ImageObject', 'url': `${site.siteDomain}/assets/opopular/favicons/96x96.png`, 'height': '96', 'width': '96' },
          parentOrganization: { '@type': 'Organization', '@id': 'https://www.gjccorp.com.br/', 'name': 'Grupo Jaime Câmara' }
        },
        webpage: {
          enabled: true,
          name: site?.pageTitle,
          description: site.pageMetadata,
          url: site.siteDomain,
          image: `${site.siteDomain}/assets/opopular/banners/BannerSocialShare.png`
        },
        website: {
          enabled: true,
          name: site?.pageTitle,
          description: site.pageMetadata,
          url: site.siteDomain,
        }
      }
    }
  }
}
